/**
 * app_setting.brand → GET /api/meta → CSS custom properties.
 *
 * The payload carries nine colors per theme (ten with the optional `critical`) plus two font
 * families. This module turns them
 * into the CSS variables src/index.css declares, and it is the ONLY place that knows the
 * mapping. It is pure, so brand-token behavior is testable without a DOM.
 *
 * Three rules it never breaks:
 *
 *  1. Only a valid hex is written into a style declaration. A brand profile is data, and data
 *     does not get to inject CSS.
 *  2. Every token pair it emits is AA-verified *from the payload*, against the other member of
 *     the pair — never against a value it cannot see. index.css is AA-checked at build time by
 *     styles/contrast.test.ts; a runtime override must not be able to quietly undo that, so a
 *     pair is emitted whole or not at all.
 *  3. A supplied color is a hue, not a role. The profiles seeded for this release use
 *     ink-weight values for some slots (`soft: #6B21A8`) and fill-weight values for the same
 *     slot in the other theme (`soft: #BFD7F5`). So each color becomes a fill and gets a
 *     computed readable ink; nothing assumes which side of the pair it was meant to be.
 */
import { AA_TEXT, contrastRatio, ensureContrast, isHex, luminance, mixHex, pillColors } from './color';
import { LINK_GROUNDS } from './layers';

/** One theme of a brand profile as /api/meta returns it. */
export interface BrandTheme {
  /** The display role: page titles and instrument numbers. */
  primary: string;
  /** The one action color. */
  interactive: string;
  /** "You are here" — the highlight fill. */
  accent: string;
  /** A pale partner of the action color; the link-text fallback on dark grounds. */
  soft: string;
  /** The page. */
  surface: string;
  success: string;
  notice: string;
  /** The ink. */
  text: string;
  divider: string;
  /** Optional (older payloads lack it): the critical fill. */
  critical?: string;
}

export interface BrandProfile {
  light: BrandTheme;
  dark: BrandTheme;
  displayFont: string;
  monoFont: string;
  /** Optional: selects a built-in `[data-brand]` block as the base to override. */
  key?: string;
}

export type ThemeMode = 'light' | 'dark';

/** WCAG 1.4.11 for non-text UI boundaries. */
const AA_UI = 3;

/** Paper steps: the ink mixed into the page (design.md §1 — elevation is paper, not shadow). */
const PAPER = { light: 0.05, dark: 0.06 } as const;
const PAPER_2 = { light: 0.09, dark: 0.1 } as const;
/** The action color as a selection tint. */
const TINT = { light: 0.08, dark: 0.18 } as const;

/** A font family name that is safe to put in a CSS declaration. */
const FONT_NAME = /^[A-Za-z][A-Za-z0-9 _-]{0,48}$/;

function fontStack(name: string | undefined, fallbacks: string): string | null {
  const trimmed = (name ?? '').trim();
  return FONT_NAME.test(trimmed) ? `'${trimmed}', ${fallbacks}` : null;
}

/**
 * Returns the CSS variables to set on <html> for one theme of a brand profile, in design.md's
 * roles. Anything the payload cannot justify is absent, leaving index.css in charge of that
 * role; every text role is AA-checked against what it sits on before it is emitted.
 */
export function brandVariables(
  profile: BrandProfile | undefined,
  mode: ThemeMode,
  /** The style-guide grounds link text sits on besides the page (ADR-027: the layers never repaint). */
  grounds: readonly string[] = Object.values(LINK_GROUNDS[mode]),
): Record<string, string> {
  const vars: Record<string, string> = {};
  const theme = profile?.[mode];

  // The display face stacks the payload's family in front of the loaded interface face, so a
  // family the app does not host yet falls back to it instead of to the browser default.
  const display = fontStack(profile?.displayFont, "'Plus Jakarta Sans', Arial, sans-serif");
  if (display) vars['--display-face'] = display;
  const mono = fontStack(profile?.monoFont, "ui-monospace, 'SF Mono', Menlo, monospace");
  if (mono) vars['--font-mono'] = mono;

  if (!theme) return vars;

  // The page and its ink anchor everything else; without both nothing can be checked.
  const page = theme.surface;
  const ink = theme.text;
  if (!isHex(page) || !isHex(ink) || contrastRatio(ink, page) < AA_TEXT) return vars;

  vars['--background'] = page;
  vars['--card'] = page;
  vars['--foreground'] = ink;
  vars['--card-foreground'] = ink;
  vars['--popover-foreground'] = ink;
  vars['--secondary-foreground'] = ink;
  vars['--accent-foreground'] = ink;

  const paper = mixHex(ink, page, PAPER[mode]);
  const paper2 = mixHex(ink, page, PAPER_2[mode]);
  vars['--muted'] = paper;
  vars['--secondary'] = paper;
  vars['--paper-2'] = paper2;
  vars['--accent'] = paper2;
  vars['--popover'] = mode === 'dark' ? paper : page;
  // Secondary text must read on the darkest paper it sits on.
  vars['--muted-foreground'] = ensureContrast(mixHex(ink, page, 0.7), paper2, AA_TEXT, ink);
  vars['--ink-3'] = mixHex(ink, page, 0.4);

  if (isHex(theme.divider)) {
    vars['--border'] = theme.divider;
    vars['--rule-strong'] = mixHex(ink, theme.divider, 0.15);
    vars['--input'] = ensureContrast(mixHex(theme.divider, ink, 0.55), page, AA_UI, ink);
  }

  // ── The one action color: a fill with white-or-ink text, and a text-safe variant ──
  if (isHex(theme.interactive)) {
    const fill = pillColors(theme.interactive, luminance(ink) < luminance(page) ? ink : page, '#ffffff');
    vars['--interactive'] = fill.bg;
    vars['--primary'] = fill.bg;
    vars['--interactive-foreground'] = fill.fg;
    vars['--primary-foreground'] = fill.fg;
    vars['--interactive-hover'] = mixHex(mode === 'dark' ? '#ffffff' : '#000000', fill.bg, 0.25);
    const text =
      contrastRatio(theme.interactive, page) >= AA_TEXT
        ? theme.interactive
        : isHex(theme.soft) && contrastRatio(theme.soft, page) >= AA_TEXT
          ? theme.soft
          : ensureContrast(theme.interactive, page, AA_TEXT, ink);
    // Link text also sits on the style guide's layers — the soft tint under a hovered or open quiet button and an
    // int tag, the selected row's raised style, the papers — which a profile never repaints (ADR-027), so the
    // text-safe variant is deepened toward the ink until it clears every one of them, not only the page
    // (PR #107 round 10: #2563EB is 5.17:1 on the page and 4.29:1 on the soft tint).
    const textOnGrounds = grounds.filter(isHex).reduce((c, ground) => ensureContrast(c, ground, AA_TEXT, ink), text);
    vars['--interactive-text'] = textOnGrounds;
    vars['--ring'] = textOnGrounds;
    vars['--accent-strong'] = textOnGrounds;
    const tint = mixHex(theme.interactive, page, TINT[mode]);
    vars['--soft'] = tint;
    vars['--soft-foreground'] = ensureContrast(textOnGrounds, tint, AA_TEXT, ink);
  }

  if (isHex(theme.primary) && contrastRatio(theme.primary, page) >= AA_TEXT) vars['--display'] = theme.primary;

  // Fills carry the darker of page/ink on light hues and white on dark ones, in both modes.
  const darkInk = luminance(ink) < luminance(page) ? ink : page;
  const fillPair = (fill: string | undefined, fillVar: string, inkVar: string) => {
    if (!isHex(fill)) return;
    const c = pillColors(fill, darkInk, '#ffffff');
    vars[fillVar] = c.bg;
    vars[inkVar] = c.fg;
  };
  fillPair(theme.accent, '--highlight', '--highlight-foreground');
  fillPair(theme.success, '--success-fill', '--success-fill-foreground');
  fillPair(theme.notice, '--notice', '--notice-foreground');
  fillPair(theme.critical, '--critical-fill', '--critical-fill-foreground');

  // Text-safe variants of the state colors: the hue, moved toward the ink only as far as AA needs.
  const textRole = (hue: string | undefined, textVar: string, onVar: string, strongVar?: string) => {
    if (!isHex(hue)) return;
    const text = ensureContrast(hue, page, AA_TEXT, ink);
    vars[textVar] = text;
    vars[onVar] = pillColors(text, darkInk, '#ffffff').fg;
    if (strongVar) vars[strongVar] = hue;
  };
  textRole(theme.success, '--success', '--success-foreground', '--success-strong');
  if (isHex(theme.notice)) vars['--notice-strong'] = ensureContrast(theme.notice, page, AA_TEXT, ink);
  textRole(theme.critical, '--destructive', '--destructive-foreground');

  // The brand mark is the dark theme's pairing in both modes: its success hue on its ground.
  const ground = profile?.dark?.surface;
  const mark = profile?.dark?.success;
  if (isHex(ground) && isHex(mark) && contrastRatio(mark, ground) >= AA_TEXT) {
    vars['--brand-mark'] = ground;
    vars['--brand-mark-foreground'] = mark;
  }

  return vars;
}

/** `[data-brand]` only selects a BUILT-IN profile block; runtime brands arrive as variables. */
const BRAND_KEY = /^[a-z0-9-]{1,32}$/;

export function brandSelector(key: string | null | undefined): string | null {
  const trimmed = (key ?? '').trim().toLowerCase();
  return BRAND_KEY.test(trimmed) ? trimmed : null;
}

interface StyledElement {
  style: { setProperty(k: string, v: string): void; removeProperty(k: string): void };
}

/**
 * Writes one theme's variables onto an element and clears any the previous profile set but
 * this one does not, so switching theme or profile never leaves a token half-applied.
 * Returns the names written, to pass back as `previous` next time.
 */
export function applyBrand(
  el: StyledElement,
  profile: BrandProfile | undefined,
  mode: ThemeMode,
  previous: readonly string[] = [],
  grounds?: readonly string[],
): string[] {
  const vars = brandVariables(profile, mode, grounds);
  for (const name of previous) if (!(name in vars)) el.style.removeProperty(name);
  for (const [name, value] of Object.entries(vars)) el.style.setProperty(name, value);
  return Object.keys(vars);
}
